import { useEffect, useState } from 'react';

import { getFreightTerminalData } from '../api/ltl.api';

import type { FreightTerminalDataResponse } from '../types/freight-responses';

type UseFreightTerminalDataParams = {
  originCode: string | null;
  destinationCode: string | null;
  scac: string | null;
  sellerFreightProviderId: number | null;
};

type UseFreightTerminalDataResult = {
  data: FreightTerminalDataResponse | null;
  isLoading: boolean;
};

/**
 * Fetches origin/destination terminal address data for a booked LTL shipment.
 * The backend requires all four params together to resolve both sides in one call,
 * so the request is skipped (data stays null) until every param is present.
 */
export const useFreightTerminalData = ({
  originCode,
  destinationCode,
  scac,
  sellerFreightProviderId,
}: UseFreightTerminalDataParams): UseFreightTerminalDataResult => {
  const [data, setData] = useState<FreightTerminalDataResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!originCode || !destinationCode || !scac || sellerFreightProviderId == null) {
      setData(null);
      return;
    }

    let isCancelled = false;
    setIsLoading(true);

    getFreightTerminalData({ originCode, destinationCode, scac, sellerFreightProviderId })
      .then((response) => {
        if (!isCancelled) setData(response);
      })
      .catch(() => {
        if (!isCancelled) setData(null);
      })
      .finally(() => {
        if (!isCancelled) setIsLoading(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [originCode, destinationCode, scac, sellerFreightProviderId]);

  return { data, isLoading };
};
